import { TutorialLayout, CodePreview } from '../../../../shared/components';
import { tutorialLevels } from '../../../../shared/config/tutorials';
import { Alert, AlertDescription } from '@voilajsx/uikit/alert';
import { Card, CardContent, CardHeader, CardTitle } from '@voilajsx/uikit/card';
import { Info } from 'lucide-react';

const LayoutSystem = () => {
  return (
    <TutorialLayout levels={tutorialLevels}>
      <div className="space-y-8">
        <div>
          <h1 className="text-3xl font-bold mb-4">Layout System</h1>
          <p className="text-lg text-muted-foreground">
            UIKit provides 6 pre-built layouts for common application patterns.
          </p>
        </div>

        <section className="space-y-4">
          <h2 className="text-2xl font-semibold">Available Layouts</h2>

          <div className="grid md:grid-cols-2 gap-4">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-lg">PageLayout</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground mb-3">
                  Marketing pages, landing pages, content sites
                </p>
                <div className="bg-muted rounded p-2 text-xs">
                  <div className="bg-primary/20 h-4 rounded mb-1">Header</div>
                  <div className="bg-primary/10 h-16 rounded mb-1">Content</div>
                  <div className="bg-primary/20 h-4 rounded">Footer</div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-lg">AdminLayout</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground mb-3">
                  Dashboards, admin panels, internal tools
                </p>
                <div className="bg-muted rounded p-2 text-xs flex">
                  <div className="bg-primary/20 w-8 rounded mr-1">Side</div>
                  <div className="flex-1">
                    <div className="bg-primary/20 h-4 rounded mb-1">Header</div>
                    <div className="bg-primary/10 h-12 rounded">Content</div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-lg">AuthLayout</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground mb-3">
                  Login, register, password reset pages
                </p>
                <div className="bg-muted rounded p-2 text-xs flex items-center justify-center h-20">
                  <div className="bg-primary/10 w-20 h-14 rounded flex items-center justify-center">
                    Form
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-lg">MobileLayout</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground mb-3">
                  Mobile-first apps, PWAs
                </p>
                <div className="bg-muted rounded p-2 text-xs max-w-[100px] mx-auto">
                  <div className="bg-primary/20 h-3 rounded mb-1">Header</div>
                  <div className="bg-primary/10 h-10 rounded mb-1">Content</div>
                  <div className="bg-primary/20 h-4 rounded">Nav</div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-lg">PopupLayout</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground mb-3">
                  Browser extensions, embedded widgets
                </p>
                <div className="bg-muted rounded p-2 text-xs">
                  <div className="bg-primary/20 h-3 rounded mb-1">Title</div>
                  <div className="bg-primary/10 h-12 rounded">Compact</div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-lg">BlankLayout</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground mb-3">
                  Custom pages, full control needed
                </p>
                <div className="bg-muted rounded p-2 text-xs h-20 flex items-center justify-center">
                  <div className="text-muted-foreground">Empty canvas</div>
                </div>
              </CardContent>
            </Card>
          </div>
        </section>

        <section className="space-y-4">
          <h2 className="text-2xl font-semibold">Basic Usage</h2>

          <CodePreview
            title="Using a Layout"
            code={`import { PageLayout } from '@voilajsx/uikit/page-layout';

function HomePage() {
  return (
    <PageLayout
      header={<Header />}
      footer={<Footer />}
    >
      <h1>Welcome</h1>
      <p>Your content here...</p>
    </PageLayout>
  );
}`}
            language="tsx"
          />
        </section>

        <section className="space-y-4">
          <h2 className="text-2xl font-semibold">Layout Import Paths</h2>

          <CodePreview
            title="Import Statements"
            code={`import { PageLayout } from '@voilajsx/uikit/page-layout';
import { AdminLayout } from '@voilajsx/uikit/admin-layout';
import { AuthLayout } from '@voilajsx/uikit/auth-layout';
import { MobileLayout } from '@voilajsx/uikit/mobile-layout';
import { PopupLayout } from '@voilajsx/uikit/popup-layout';
import { BlankLayout } from '@voilajsx/uikit/blank-layout';`}
            language="tsx"
          />
        </section>

        <Alert>
          <Info className="w-4 h-4" />
          <AlertDescription>
            Layouts handle responsive behavior, scroll management, and consistent spacing automatically.
          </AlertDescription>
        </Alert>

        <section className="space-y-4">
          <h2 className="text-2xl font-semibold">Choosing the Right Layout</h2>

          <div className="bg-muted/50 rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left p-3 font-semibold">Use Case</th>
                  <th className="text-left p-3 font-semibold">Layout</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-border">
                  <td className="p-3 text-muted-foreground">Marketing website</td>
                  <td className="p-3 font-mono">PageLayout</td>
                </tr>
                <tr className="border-b border-border">
                  <td className="p-3 text-muted-foreground">Documentation site</td>
                  <td className="p-3 font-mono">PageLayout + sidebar</td>
                </tr>
                <tr className="border-b border-border">
                  <td className="p-3 text-muted-foreground">Admin dashboard</td>
                  <td className="p-3 font-mono">AdminLayout</td>
                </tr>
                <tr className="border-b border-border">
                  <td className="p-3 text-muted-foreground">SaaS application</td>
                  <td className="p-3 font-mono">AdminLayout</td>
                </tr>
                <tr className="border-b border-border">
                  <td className="p-3 text-muted-foreground">Login page</td>
                  <td className="p-3 font-mono">AuthLayout</td>
                </tr>
                <tr className="border-b border-border">
                  <td className="p-3 text-muted-foreground">Mobile app</td>
                  <td className="p-3 font-mono">MobileLayout</td>
                </tr>
                <tr className="border-b border-border">
                  <td className="p-3 text-muted-foreground">Chrome extension</td>
                  <td className="p-3 font-mono">PopupLayout</td>
                </tr>
                <tr>
                  <td className="p-3 text-muted-foreground">Error pages (404, 500)</td>
                  <td className="p-3 font-mono">BlankLayout</td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        <section className="space-y-4">
          <h2 className="text-2xl font-semibold">Combining Layouts</h2>
          <p className="text-muted-foreground">
            In larger applications, different routes often use different layouts:
          </p>

          <CodePreview
            title="Multiple Layouts in Routes"
            code={`import { Routes, Route } from 'react-router-dom';
import { PageLayout } from '@voilajsx/uikit/page-layout';
import { AdminLayout } from '@voilajsx/uikit/admin-layout';
import { AuthLayout } from '@voilajsx/uikit/auth-layout';

function App() {
  return (
    <Routes>
      {/* Public pages */}
      <Route path="/" element={
        <PageLayout header={<Header />}>
          <HomePage />
        </PageLayout>
      } />

      {/* Auth pages */}
      <Route path="/login" element={
        <AuthLayout>
          <LoginPage />
        </AuthLayout>
      } />

      {/* Dashboard pages */}
      <Route path="/dashboard/*" element={
        <AdminLayout sidebar={<Sidebar />}>
          <DashboardRoutes />
        </AdminLayout>
      } />
    </Routes>
  );
}`}
            language="tsx"
          />
        </section>

        <section className="space-y-4">
          <h2 className="text-2xl font-semibold">Next Steps</h2>
          <p className="text-muted-foreground">
            In the following lessons, we'll explore each layout in detail, starting with PageLayout.
          </p>
        </section>
      </div>
    </TutorialLayout>
  );
};

export default LayoutSystem;
